import type { ComponentProps, HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";

import { cn } from "../../lib/utils";

// 用 ComponentProps 而不是 InputHTMLAttributes：React 19 允许把 ref 当普通 prop 透传，
// 这样工具条里的输入框可以直接 focus。
export function Input({ className, ...props }: ComponentProps<"input">) {
  return (
    <input
      className={cn(
        // 焦点环用不透明的品牌色：键盘用户必须看得见自己在哪个输入框里
        "h-9 w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-1 text-sm disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export function Select({ className, ...props }: ComponentProps<"select">) {
  return (
    <select
      className={cn(
        "h-9 w-full rounded-md border border-[var(--color-border)] bg-white px-2 text-sm",
        className,
      )}
      {...props}
    />
  );
}

export function Badge({
  className,
  tone = "muted",
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: "muted" | "ok" | "warn" | "danger" }) {
  const tones = {
    muted: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
    ok: "bg-emerald-50 text-emerald-700",
    warn: "bg-amber-50 text-amber-700",
    danger: "bg-red-50 text-red-700",
  } as const;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        tones[tone],
        className,
      )}
      {...props}
    />
  );
}

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return <table className={cn("w-full border-collapse text-sm", className)} {...props} />;
}

export function Th({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "border-b border-[var(--color-border)] px-3 py-2 text-left text-xs font-medium text-[var(--color-muted-foreground)]",
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cn("border-b border-[var(--color-border)] px-3 py-2 align-middle", className)}
      {...props}
    />
  );
}

export function Separator({ className }: { className?: string }) {
  return <div className={cn("h-px w-full bg-[var(--color-border)]", className)} />;
}
